<template>
  <section id="explorer" class="explorer">

    <!-- Section header -->
    <div class="section-header reveal-up" ref="sectionHeader">
      <div class="section-eyebrow">
        <span class="eyebrow-dot"></span>
        <span>Interactive Explorer</span>
      </div>
      <h2 class="section-title">Browse 3D Objects</h2>
      <p class="section-desc">Explore 3D assets with multi-level AI captions across 7 source datasets.</p>
    </div>

    <!-- Dataset tabs -->
    <div class="dataset-tabs reveal-up" ref="tabsRow">
      <button
        v-for="(tab, i) in tabs" :key="i"
        class="dataset-tab" :class="{ active: activeTab === i }"
        @click="switchTab(i)"
      >
        <span class="tab-icon">{{ tab.icon }}</span>
        <span class="tab-name">{{ tab.name }}</span>
        <span class="tab-count" v-if="activeTab === i && filteredModels.length">{{ sortedModels.length }}</span>
      </button>
    </div>

    <!-- Main layout -->
    <div class="explorer-layout">

      <!-- LVIS Sidebar -->
      <transition name="sidebar-slide">
        <div class="lvis-sidebar" v-if="activeTab === 0">
          <div class="sidebar-header">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            <span>LVIS Categories</span>
          </div>
          <div class="sidebar-list">
            <button class="sidebar-cat" :class="{ active: selectedCategory === '' }" @click="clearCategory">All Objects</button>
            <button v-for="(cat, ci) in categories" :key="ci" class="sidebar-cat" :class="{ active: selectedCategory === cat }" @click="filterByCategory(cat)">{{ cat }}</button>
          </div>
        </div>
      </transition>

      <!-- Main content -->
      <div class="explorer-main">

        <!-- Toolbar: search + sort + view toggle -->
        <div class="toolbar">
          <div class="search-inner" :class="{ focused: searchFocused }">
            <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="'Search ' + currentTabName + ' objects…'"
              class="search-input"
              @input="applyFilters"
              @focus="searchFocused = true"
              @blur="searchFocused = false"
              @keydown.enter="applyFilters"
              autocomplete="off"
            />
            <button v-if="searchQuery" class="search-clear" @click="clearSearch">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
            <div class="search-loader" v-if="loading"><span></span><span></span><span></span></div>
          </div>

          <!-- Sort -->
          <div class="sort-wrap">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M7 12h10M11 18h2"/></svg>
            <select v-model="sortBy" class="sort-select">
              <option value="default">Default</option>
              <option value="name_asc">Name A→Z</option>
              <option value="name_desc">Name Z→A</option>
              <option value="richness">Most Annotated</option>
            </select>
          </div>

          <!-- View toggle -->
          <div class="view-toggle">
            <button class="view-btn" :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'" title="Grid view">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
            </button>
            <button class="view-btn" :class="{ active: viewMode === 'list' }" @click="viewMode = 'list'" title="List view">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
          </div>
        </div>

        <!-- Results meta -->
        <div class="results-meta">
          <span class="results-dataset">{{ currentTabName }}</span>
          <span class="results-sep">·</span>
          <span class="results-count">{{ sortedModels.length }} objects</span>
          <span v-if="selectedCategory" class="results-filter">
            <span class="results-sep">·</span>
            {{ selectedCategory }}
            <button class="clear-filter" @click="clearCategory">✕</button>
          </span>
          <span class="results-sep" v-if="sortBy !== 'default'">·</span>
          <span class="results-sort" v-if="sortBy !== 'default'">sorted by {{ sortLabel }}</span>
          <!-- Keyboard hint -->
          <span class="kbd-hint">
            <kbd>←</kbd><kbd>→</kbd> navigate
            <kbd>1</kbd>–<kbd>5</kbd> level
            <kbd>Esc</kbd> close
          </span>
        </div>

        

        <!-- Loading skeleton -->
        <div class="model-grid" v-if="loading && filteredModels.length === 0">
          <div v-for="n in 8" :key="n" class="model-card skeleton">
            <div class="skeleton-thumb"></div>
            <div class="card-body"><div class="skeleton-line w70"></div><div class="skeleton-line w50"></div></div>
          </div>
        </div>

        <!-- GRID view -->
        <div class="model-grid" v-else-if="sortedModels.length && viewMode === 'grid'">
          <div
            v-for="(model, i) in sortedModels" :key="model.id"
            class="model-card" :style="{ '--delay': (i % 12) * 55 + 'ms' }"
            @click="openModelPopup(model, i)"
          >
            <div class="card-thumb">
              <img :src="thumbSrc(model)" :alt="model.name" loading="lazy"
                @load="e => e.target.classList.add('loaded')"
                @error="e => e.target.src = 'https://placehold.co/300x220/07111a/5b7fff?text=3D'"
              />
              <div class="card-thumb-overlay">
                <div class="card-view-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  View
                </div>
              </div>
              <div class="card-levels">
                <span v-for="l in 5" :key="l" class="level-dot" :class="{ active: !!model['level' + l] }"></span>
              </div>
              <div class="card-index">#{{ i + 1 }}</div>
            </div>
            <div class="card-body">
              <h3 class="card-name">{{ formatName(model.name) }}</h3>
              <p class="card-preview">{{ getPreview(model) }}</p>
              <div class="card-footer-row">
                <span class="card-uid">{{ model.id ? model.id.slice(0, 10) + '…' : '' }}</span>
                <span class="card-arrow">→</span>
              </div>
            </div>
          </div>
        </div>

        <!-- LIST view -->
        <div class="model-list" v-else-if="sortedModels.length && viewMode === 'list'">
          <div
            v-for="(model, i) in sortedModels" :key="model.id"
            class="list-row" :style="{ '--delay': (i % 20) * 30 + 'ms' }"
            @click="openModelPopup(model, i)"
          >
            <div class="list-thumb">
              <img :src="thumbSrc(model)" :alt="model.name" loading="lazy"
                @load="e => e.target.classList.add('loaded')"
                @error="e => e.target.src = 'https://placehold.co/80x60/07111a/5b7fff?text=3D'"
              />
            </div>
            <div class="list-info">
              <div class="list-name">{{ formatName(model.name) }}</div>
              <div class="list-preview">{{ getPreview(model) }}</div>
            </div>
            <div class="list-levels">
              <span v-for="l in 5" :key="l" class="level-dot" :class="{ active: !!model['level' + l] }"></span>
            </div>
            <div class="list-uid">{{ model.id ? model.id.slice(0, 12) + '…' : '' }}</div>
            <div class="list-arrow">→</div>
          </div>
        </div>

        <!-- Empty state -->
        <div class="empty-state" v-else-if="!loading">
          <div class="empty-icon">🔍</div>
          <p>No results found<span v-if="searchQuery"> for "{{ searchQuery }}"</span>.</p>
          <button class="empty-clear" @click="clearSearch">Clear filters</button>
        </div>

      
      
      
      <!-- Pagination -->
<div class="pagination" v-if="totalPages > 1 && !searchQuery">
  <button class="page-btn nav-btn" @click="fetchPage(currentPage - 1)" :disabled="currentPage === 1">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
  </button>

  <template v-for="(p, i) in visiblePages">
    <span v-if="p === '...'" :key="'ellipsis-' + i" class="page-ellipsis">…</span>
    <button
      v-else :key="p"
      class="page-btn" :class="{ active: currentPage === p }"
      @click="fetchPage(p)"
    >{{ p }}</button>
  </template>

  <button class="page-btn nav-btn" @click="fetchPage(currentPage + 1)" :disabled="currentPage === totalPages">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
  </button>

  <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
</div>
      </div>
    
    
    </div>

    <!-- Popup -->
    <ModelPopup
      v-if="popupVisible"
      :visible="popupVisible"
      :modelData="selectedModel"
      :type="activeTab"
      :modelIndex="selectedIndex"
      :totalModels="sortedModels.length"
      @close="popupVisible = false"
      @prev="navigatePopup(-1)"
      @next="navigatePopup(1)"
    />
  </section>
</template>

<script>
import ModelPopup from './Popup.vue';
import { db } from '../firebase.js';
import {
  collection, getDocs, query, orderBy, limit,
  startAt, startAfter, endAt, where, documentId
} from 'firebase/firestore';

const DATASETS = [
  { name: "Objaverse",    icon: "🌐", collection: "Marvel2.0",    thumb: (id) => `https://objaverse-thumbnail-images.s3.us-west-2.amazonaws.com/${id}.jpg` },
  { name: "ShapeNet",     icon: "🔷", collection: "Shapenet1000", thumb: (id) => `assets/shapenet/output/${id}.png` },
  { name: "Pix3D",        icon: "📦", collection: "PIX3D",        thumb: (id) => `assets/PIX/output/${id}.png` },
  { name: "OmniObject3D", icon: "🎯", collection: "Omni",         thumb: (id) => `assets/Omni/output/${id}.png` },
  { name: "Toys4K",       icon: "🧸", collection: "Toys4K",       thumb: (id) => `assets/Toys/output/${id}.png` },
  { name: "GSO",          icon: "🏠", collection: "GSO2",         thumb: (id) => `assets/GSO2/output/${id}.png` },
  { name: "ABO",          icon: "🛋️",  collection: "ABO",          thumb: (id) => `assets/ABO/output/${id}.png` },
];

export default {
  name: 'MarvelGrid',
  components: { ModelPopup },

  // ─────────────────────────────────────────────
  data() {
    return {
      tabs: DATASETS,
      activeTab: 0,
      searchQuery: '',
      searchFocused: false,
      loading: false,
      filteredModels: [],
      categories: [],
      lvisAnnotations: {},
      selectedCategory: '',
      popupVisible: false,
      selectedModel: null,
      selectedIndex: 0,
      viewMode: 'grid',
      sortBy: 'default',
      // Pagination
      currentPage: 1,
      pageSize: 20,
      totalCounts: {
        Objaverse: 19900,
        ShapeNet: 310,
        Pix3D: 456,
        OmniObject3D: 102,
        Toys4K: 100,
        GSO: 0,
        ABO: 100,
      },
      pageCursors: {},
    };
  },

  // ─────────────────────────────────────────────
  computed: {
    currentTabName() {
      return DATASETS[this.activeTab]?.name || '';
    },
    sortLabel() {
      return { name_asc: 'A→Z', name_desc: 'Z→A', richness: 'most annotated' }[this.sortBy] || '';
    },
    sortedModels() {
      const m = [...this.filteredModels];
      if (this.sortBy === 'name_asc')  return m.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      if (this.sortBy === 'name_desc') return m.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      if (this.sortBy === 'richness')  return m.sort((a, b) => this.richness(b) - this.richness(a));
      return m;
    },
    totalPages() {
      const name = DATASETS[this.activeTab]?.name;
      return Math.ceil((this.totalCounts[name] || 0) / this.pageSize);
    },
    visiblePages() {
      const total = this.totalPages;
      const cur = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      if (cur <= 4)          return [1, 2, 3, 4, 5, '...', total];
      if (cur >= total - 3)  return [1, '...', total-4, total-3, total-2, total-1, total];
      return [1, '...', cur-1, cur, cur+1, '...', total];
    },
  },

  // ─────────────────────────────────────────────
  watch: {
    activeTab() {
      this.filteredModels = [];
      this.searchQuery = '';
      this.selectedCategory = '';
      this.currentPage = 1;
      this.pageCursors = {};
      this.fetchInitialModels();
    },
  },

  // ─────────────────────────────────────────────
  async mounted() {
    this.setupReveal();
    this.setupKeyboard();
    await this.fetchLVISAnnotations();
    await this.fetchInitialModels();
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this._keyHandler);
  },

  // ─────────────────────────────────────────────
  methods: {

    // ── Helpers ──────────────────────────────
    formatName(name) {
      if (!name) return '';
      return name.replace(/[_\W]+/g, ' ').replace(/\s+/g, ' ').trim()
        .replace(/\b\w/g, c => c.toUpperCase());
    },
    thumbSrc(model) {
      return DATASETS[this.activeTab]?.thumb(model.id) || '';
    },
    getPreview(model) {
      const t = model.level4 || model.level3 || model.level5 || '';
      return t.length > 90 ? t.slice(0, 90) + '…' : t;
    },
    richness(model) {
      return [1,2,3,4,5].filter(l => !!model['level' + l]).length;
    },
    mapDoc(doc) {
      const d = doc.data();
      return {
        id: doc.id,
        name: d.name ?? d.base ?? '',
        level5: d.level_5 || '',
        level4: d.level_4 || '',
        level3: d.level_3 || '',
        level2: d.level_2 || '',
        level1: d.level_1 || '',
      };
    },
    switchTab(i) {
      this.activeTab = i;
    },

    // ── LVIS ─────────────────────────────────
    async fetchLVISAnnotations() {
      try {
        const snap = await getDocs(query(collection(db, 'Indexing/Lvis_annotation/CategoryName')));
        const a = {};
        snap.forEach(doc => { a[doc.id] = doc.data().filtered_ids || []; });
        this.lvisAnnotations = a;
        this.categories = Object.keys(a);
      } catch (err) { console.error('LVIS fetch error:', err); }
    },
    filterByCategory(cat) {
      this.selectedCategory = cat;
      if (this.activeTab === 0) this.filterFirebaseByLVIS(cat);
    },
    clearCategory() {
      this.selectedCategory = '';
      this.fetchInitialModels();
    },
    async filterFirebaseByLVIS(cat) {
      try {
        const ids = (this.lvisAnnotations[cat] || []).slice(0, 10);
        if (!ids.length) { this.filteredModels = []; return; }
        const snap = await getDocs(query(
          collection(db, 'Marvel2.0'),
          where(documentId(), 'in', ids)
        ));
        this.filteredModels = snap.docs.map(this.mapDoc);
      } catch (err) { console.error('LVIS filter error:', err); }
    },

    // ── Pagination ────────────────────────────
    async fetchInitialModels() {
      this.currentPage = 1;
      this.pageCursors = {};
      await this.fetchPage(1);
    },

    async fetchPage(page) {
      this.loading = true;
      try {
        const col = DATASETS[this.activeTab].collection;
        let q;
        if (page === 1) {
          q = query(collection(db, col), orderBy('__name__'), limit(this.pageSize));
        } else {
          const cursor = this.pageCursors[page - 1];
          if (!cursor) {
            await this.fetchPageSequential(page);
            return;
          }
          q = query(collection(db, col), orderBy('__name__'), startAfter(cursor), limit(this.pageSize));
        }
        const snap = await getDocs(q);
        this.filteredModels = snap.docs.map(this.mapDoc);
        if (snap.docs.length > 0) {
          this.pageCursors[page] = snap.docs[snap.docs.length - 1];
        }
        this.currentPage = page;
        // Scroll back to explorer top
        const el = document.getElementById('explorer');
        if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
      } catch (err) {
        console.error('Fetch page error:', err);
      } finally {
        this.loading = false;
      }
    },

    async fetchPageSequential(targetPage) {
      const col = DATASETS[this.activeTab].collection;
      let cursor = null;
      let lastCachedPage = 0;
      // Find the furthest cached cursor
      for (let p = targetPage - 1; p >= 1; p--) {
        if (this.pageCursors[p]) {
          lastCachedPage = p;
          cursor = this.pageCursors[p];
          break;
        }
      }
      // Walk forward from there
      for (let p = lastCachedPage + 1; p <= targetPage; p++) {
        const q = cursor
          ? query(collection(db, col), orderBy('__name__'), startAfter(cursor), limit(this.pageSize))
          : query(collection(db, col), orderBy('__name__'), limit(this.pageSize));
        const snap = await getDocs(q);
        if (snap.docs.length > 0) {
          cursor = snap.docs[snap.docs.length - 1];
          this.pageCursors[p] = cursor;
        }
        if (p === targetPage) {
          this.filteredModels = snap.docs.map(this.mapDoc);
        }
      }
      this.currentPage = targetPage;
      this.loading = false;
      const el = document.getElementById('explorer');
      if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    },

    // ── Search ────────────────────────────────
    async searchFirebaseModels() {
      this.loading = true;
      try {
        const col = DATASETS[this.activeTab].collection;
        const term = this.searchQuery.trim();
        if (!term) { await this.fetchInitialModels(); return; }
        const orderField = this.activeTab === 0 ? 'name' : 'base';
        const [nameSnap, level5Snap] = await Promise.all([
          getDocs(query(collection(db, col), orderBy(orderField), startAt(term), endAt(term + '\uf8ff'), limit(10))),
          getDocs(query(collection(db, col), orderBy('level_5'), startAt(term), endAt(term + '\uf8ff'), limit(5))),
        ]);
        const map = new Map();
        nameSnap.forEach(doc => map.set(doc.id, this.mapDoc(doc)));
        level5Snap.forEach(doc => { if (!map.has(doc.id)) map.set(doc.id, this.mapDoc(doc)); });
        this.filteredModels = Array.from(map.values()).slice(0, 20);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        this.loading = false;
      }
    },
    async applyFilters() {
      if (this.searchQuery.trim()) await this.searchFirebaseModels();
      else await this.fetchInitialModels();
    },
    clearSearch() {
      this.searchQuery = '';
      this.fetchInitialModels();
    },

    // ── Popup & navigation ────────────────────
    openModelPopup(model, index) {
      this.selectedModel = model;
      this.selectedIndex = index;
      this.popupVisible = true;
    },
    navigatePopup(dir) {
      const next = this.selectedIndex + dir;
      if (next < 0 || next >= this.sortedModels.length) return;
      this.selectedIndex = next;
      this.selectedModel = this.sortedModels[next];
    },

    // ── Keyboard shortcuts ────────────────────
    setupKeyboard() {
      this._keyHandler = (e) => {
        if (!this.popupVisible) return;
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this.navigatePopup(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.navigatePopup(1); }
        if (e.key === 'Escape')     { this.popupVisible = false; }
      };
      window.addEventListener('keydown', this._keyHandler);
    },

    // ── Reveal animation ──────────────────────
    setupReveal() {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add('visible'); observer.unobserve(e.target); }
        });
      }, { threshold: 0.08 });
      this.$nextTick(() => {
        [this.$refs.sectionHeader, this.$refs.tabsRow]
          .filter(Boolean).forEach(el => observer.observe(el));
      });
    },
  },
};
</script>

<style scoped>
.explorer { padding: 5rem 2rem 8rem; max-width: 1400px; margin: 0 auto; }

/* Section header */
.section-header { text-align: center; margin-bottom: 2.5rem; }
.section-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: 'JetBrains Mono', monospace; font-size: 0.7rem;
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--accent); margin-bottom: 1rem;
}
.eyebrow-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--accent); }
.section-title {
  font-family: 'Bebas Neue', sans-serif; font-size: clamp(2.5rem, 6vw, 5rem); letter-spacing: 0.05em;
  background: linear-gradient(135deg, var(--text), var(--accent));
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; margin-bottom: 0.75rem;
}
.section-desc { color: var(--text2); font-size: 1rem; max-width: 520px; margin: 0 auto; line-height: 1.7; }

/* Dataset tabs */
.dataset-tabs {
  display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 2rem;
  padding: 6px; background: var(--surface); border: 1px solid var(--border);
  border-radius: 16px; justify-content: center;
}
.dataset-tab {
  display: flex; align-items: center; gap: 7px; padding: 8px 18px; border-radius: 10px;
  border: 1px solid transparent; background: none; color: var(--text2);
  font-family: 'Google Sans', 'Syne', sans-serif; font-size: 0.85rem; font-weight: 600;
  cursor: pointer; transition: all var(--transition); white-space: nowrap;
}
.dataset-tab:hover { background: var(--surface2); color: var(--text); }
.dataset-tab.active {
  background: rgba(91,127,255,0.12); border-color: var(--accent); color: var(--text);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.06);
}
.tab-icon { font-size: 0.95rem; }
.tab-count {
  font-family: 'JetBrains Mono', monospace; font-size: 0.65rem;
  background: var(--accent); color: #fff; padding: 1px 6px; border-radius: 100px;
}

/* Layout */
.explorer-layout { display: flex; gap: 1.5rem; align-items: flex-start; }

/* Sidebar */
.lvis-sidebar {
  flex-shrink: 0; width: 200px; background: var(--surface); border: 1px solid var(--border);
  border-radius: 16px; overflow: hidden; position: sticky; top: 88px;
  max-height: calc(100vh - 110px); display: flex; flex-direction: column;
}
.sidebar-header {
  display: flex; align-items: center; gap: 8px; padding: 14px 16px;
  font-family: 'JetBrains Mono', monospace; font-size: 0.68rem; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--accent); border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.sidebar-list { overflow-y: auto; padding: 8px; flex: 1; }
.sidebar-list::-webkit-scrollbar { width: 3px; }
.sidebar-list::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 2px; }
.sidebar-cat {
  display: block; width: 100%; padding: 7px 10px; text-align: left;
  background: none; border: 1px solid transparent; border-radius: 8px;
  color: var(--text2); font-family: 'Google Sans', sans-serif; font-size: 0.78rem;
  cursor: pointer; transition: all var(--transition); margin-bottom: 2px;
}
.sidebar-cat:hover { background: var(--surface2); color: var(--text); }
.sidebar-cat.active { background: rgba(91,127,255,0.12); border-color: var(--accent); color: var(--text); font-weight: 600; }
.sidebar-slide-enter-active, .sidebar-slide-leave-active { transition: width 0.35s ease, opacity 0.35s ease; overflow: hidden; }
.sidebar-slide-enter, .sidebar-slide-leave-to { width: 0; opacity: 0; }
.sidebar-slide-enter-to, .sidebar-slide-leave { width: 200px; opacity: 1; }

.explorer-main { flex: 1; min-width: 0; }

/* ── Toolbar ── */
.toolbar {
  display: flex; align-items: center; gap: 10px; margin-bottom: 1rem;
}
.search-inner {
  flex: 1; display: flex; align-items: center; gap: 10px;
  background: var(--surface2); border: 1px solid var(--border2);
  border-radius: 100px; padding: 10px 18px;
  transition: border-color var(--transition), box-shadow var(--transition);
}
.search-inner.focused { border-color: var(--accent); box-shadow: 0 0 0 3px var(--glow); }
.search-icon { color: var(--text3); flex-shrink: 0; }
.search-input {
  flex: 1; background: none; border: none; outline: none;
  color: var(--text); font-family: 'Google Sans', sans-serif; font-size: 0.9rem;
}
.search-input::placeholder { color: var(--text3); }
.search-clear {
  background: none; border: none; cursor: pointer; color: var(--text3);
  padding: 2px; line-height: 0; transition: color var(--transition);
}
.search-clear:hover { color: var(--text); }
.search-loader { display: flex; gap: 4px; align-items: center; }
.search-loader span {
  width: 4px; height: 4px; border-radius: 50%; background: var(--accent);
  animation: loaderBounce 1s ease-in-out infinite;
}
.search-loader span:nth-child(2) { animation-delay: 0.15s; }
.search-loader span:nth-child(3) { animation-delay: 0.3s; }
@keyframes loaderBounce { 0%,80%,100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }

/* Sort */
.sort-wrap {
  display: flex; align-items: center; gap: 7px;
  background: var(--surface2); border: 1px solid var(--border2);
  border-radius: 10px; padding: 8px 12px; color: var(--text3); flex-shrink: 0;
}
.sort-select {
  background: none; border: none; outline: none; color: var(--text);
  font-family: 'Google Sans', sans-serif; font-size: 0.8rem; cursor: pointer;
}
.sort-select option { background: var(--bg2); color: var(--text); }

/* View toggle */
.view-toggle {
  display: flex; gap: 2px;
  background: var(--surface2); border: 1px solid var(--border2);
  border-radius: 10px; padding: 4px; flex-shrink: 0;
}
.view-btn {
  width: 30px; height: 30px; border: none; border-radius: 7px;
  background: none; cursor: pointer; color: var(--text3);
  display: flex; align-items: center; justify-content: center;
  transition: all var(--transition);
}
.view-btn:hover { color: var(--text); background: var(--surface); }
.view-btn.active { background: var(--surface); color: var(--accent); }

/* Results meta */
.results-meta {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  font-family: 'JetBrains Mono', monospace; font-size: 0.72rem;
  color: var(--text3); letter-spacing: 0.06em; margin-bottom: 1.25rem;
}
.results-dataset { color: var(--accent2); font-weight: 600; }
.results-count { color: var(--accent); }
.results-sep { color: var(--border2); }
.results-filter { display: flex; align-items: center; gap: 6px; color: var(--text2); }
.results-sort { color: var(--text2); }
.clear-filter { background: none; border: none; color: var(--text3); cursor: pointer; font-size: 0.75rem; padding: 0 2px; }
.clear-filter:hover { color: var(--text); }

/* Keyboard hint */
.kbd-hint {
  margin-left: auto; display: flex; align-items: center; gap: 4px;
  font-size: 0.65rem; color: var(--text3); letter-spacing: 0;
}
kbd {
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--surface2); border: 1px solid var(--border2);
  border-radius: 4px; padding: 1px 5px; font-family: 'JetBrains Mono', monospace;
  font-size: 0.62rem; color: var(--text2); line-height: 1.4;
}

/* ── Grid view ── */
.model-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.1rem; }
.model-card {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); overflow: hidden; cursor: pointer;
  transition: transform var(--transition), border-color var(--transition), box-shadow var(--transition);
  animation: cardIn 0.45s ease both; animation-delay: var(--delay, 0ms);
}
.model-card:hover { transform: translateY(-5px); border-color: var(--accent); box-shadow: 0 14px 36px rgba(0,0,0,0.3), 0 0 16px var(--glow); }
@keyframes cardIn { from { opacity: 0; transform: translateY(20px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
.card-thumb { position: relative; overflow: hidden; aspect-ratio: 4/3; background: var(--bg2); }
.card-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; opacity: 0; transition: opacity 0.4s ease, transform 0.4s ease; }
.card-thumb img.loaded { opacity: 1; }
.model-card:hover .card-thumb img { transform: scale(1.04); }
.card-thumb-overlay {
  position: absolute; inset: 0; background: rgba(3,5,15,0.55);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; transition: opacity var(--transition); backdrop-filter: blur(3px);
}
.model-card:hover .card-thumb-overlay { opacity: 1; }
.card-view-btn {
  display: flex; align-items: center; gap: 7px; padding: 8px 16px;
  background: rgba(91,127,255,0.18); border: 1px solid rgba(91,127,255,0.45);
  color: #fff; border-radius: 100px; font-size: 0.78rem; font-weight: 600;
  backdrop-filter: blur(8px); box-shadow: inset 0 1px 0 rgba(255,255,255,0.1);
}
.card-levels { position: absolute; top: 9px; right: 9px; display: flex; gap: 3px; }
.level-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--border2); border: 1px solid rgba(255,255,255,0.08); }
.level-dot.active { background: var(--accent3); box-shadow: 0 0 5px rgba(0,229,255,0.7); }
.card-index {
  position: absolute; top: 9px; left: 9px;
  font-family: 'JetBrains Mono', monospace; font-size: 0.58rem;
  color: rgba(255,255,255,0.5); background: rgba(0,0,0,0.3);
  padding: 2px 5px; border-radius: 4px; backdrop-filter: blur(4px);
}
.card-body { padding: 12px 14px 10px; }
.card-name { font-family: 'Google Sans', sans-serif; font-size: 0.88rem; font-weight: 600; color: var(--text); margin-bottom: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.card-preview { font-family: 'Google Sans', sans-serif; font-size: 0.72rem; color: var(--text2); line-height: 1.5; height: 2.2em; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.card-footer-row { display: flex; justify-content: space-between; align-items: center; margin-top: 9px; padding-top: 9px; border-top: 1px solid var(--border); }
.card-uid { font-family: 'JetBrains Mono', monospace; font-size: 0.6rem; color: var(--text3); }
.card-arrow { color: var(--accent); font-size: 0.85rem; transition: transform var(--transition); }
.model-card:hover .card-arrow { transform: translateX(4px); }

/* ── List view ── */
.model-list { display: flex; flex-direction: column; gap: 6px; }
.list-row {
  display: flex; align-items: center; gap: 14px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 12px; padding: 10px 14px; cursor: pointer;
  transition: all var(--transition);
  animation: cardIn 0.35s ease both; animation-delay: var(--delay, 0ms);
}
.list-row:hover { border-color: var(--accent); background: var(--surface2); transform: translateX(3px); }
.list-thumb {
  width: 64px; height: 48px; border-radius: 8px; overflow: hidden;
  flex-shrink: 0; background: var(--bg2);
}
.list-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; opacity: 0; transition: opacity 0.4s ease; }
.list-thumb img.loaded { opacity: 1; }
.list-info { flex: 1; min-width: 0; }
.list-name { font-family: 'Google Sans', sans-serif; font-size: 0.88rem; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 3px; }
.list-preview { font-family: 'Google Sans', sans-serif; font-size: 0.72rem; color: var(--text2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.list-levels { display: flex; gap: 3px; flex-shrink: 0; }
.list-uid { font-family: 'JetBrains Mono', monospace; font-size: 0.6rem; color: var(--text3); flex-shrink: 0; }
.list-arrow { color: var(--accent); font-size: 0.85rem; flex-shrink: 0; transition: transform var(--transition); }
.list-row:hover .list-arrow { transform: translateX(4px); }

/* Skeleton */
.skeleton { pointer-events: none; }
.skeleton-thumb { aspect-ratio: 4/3; background: linear-gradient(90deg, var(--surface) 25%, var(--surface2) 50%, var(--surface) 75%); background-size: 200% 100%; animation: shimmer 1.4s ease-in-out infinite; }
.skeleton-line { height: 10px; border-radius: 5px; margin-bottom: 8px; background: linear-gradient(90deg, var(--surface) 25%, var(--surface2) 50%, var(--surface) 75%); background-size: 200% 100%; animation: shimmer 1.4s ease-in-out infinite; }
.w70 { width: 70%; } .w50 { width: 50%; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* Empty state */
.empty-state { padding: 4rem 2rem; text-align: center; color: var(--text2); font-size: 0.95rem; }
.empty-icon { font-size: 2.5rem; margin-bottom: 1rem; }
.empty-clear { margin-top: 1rem; padding: 8px 20px; background: var(--surface2); border: 1px solid var(--border2); border-radius: 8px; color: var(--text); cursor: pointer; font-family: 'Google Sans', sans-serif; font-size: 0.85rem; transition: all var(--transition); }
.empty-clear:hover { border-color: var(--accent); color: var(--accent); }

/* Pagination */
.pagination {
  display: flex; align-items: center; justify-content: center;
  gap: 4px; padding: 2rem 0 1rem; flex-wrap: wrap;
}
.page-btn {
  min-width: 36px; height: 36px; padding: 0 8px;
  border: 1px solid var(--border2); border-radius: 8px;
  background: var(--surface); color: var(--text2);
  font-family: 'JetBrains Mono', monospace; font-size: 0.78rem;
  cursor: pointer; transition: all var(--transition);
  display: flex; align-items: center; justify-content: center;
}
.page-btn:hover:not(:disabled) {
  border-color: var(--accent); color: var(--text);
  background: rgba(91,127,255,0.08);
}
.page-btn.active {
  background: var(--accent); border-color: var(--accent);
  color: #fff; font-weight: 700;
  box-shadow: 0 0 14px var(--glow);
}
.page-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.page-btn.nav-btn { border-radius: 10px; }
.page-ellipsis {
  color: var(--text3); font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem; padding: 0 4px; line-height: 36px;
}
.page-info {
  margin-left: 12px; font-family: 'JetBrains Mono', monospace;
  font-size: 0.68rem; color: var(--text3); letter-spacing: 0.06em;
}

/* Reveal */
.reveal-up { opacity: 0; transform: translateY(28px); transition: opacity 0.7s ease, transform 0.7s ease; }
.reveal-up.visible { opacity: 1; transform: none; }

/* Responsive */
@media (max-width: 1100px) { .model-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 860px) { .model-grid { grid-template-columns: repeat(2, 1fr); } .lvis-sidebar { display: none; } }
@media (max-width: 640px) { .toolbar { flex-wrap: wrap; } .sort-wrap, .view-toggle { flex-shrink: 0; } .kbd-hint { display: none; } }
@media (max-width: 520px) { .model-grid { grid-template-columns: 1fr; } .dataset-tabs { gap: 4px; } .dataset-tab { padding: 7px 12px; font-size: 0.78rem; } .explorer { padding: 3rem 1rem 6rem; } }
</style>